import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from "@mui/material";
import Layout from "../components/layout/LegalLayout";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import axios from "axios";
import config from "../config.js";
import { useSelector } from "react-redux";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const CustomTimeline = ({ milestones }) => {
  return (
    <div style={{ position: 'relative', paddingLeft: '20px' }}>
      {milestones.map((milestone, index) => (
        <div 
          key={index}
          style={{ 
            position: 'relative', 
            paddingBottom: '20px',
            borderLeft: '2px solid #e0e0e0'
          }}
        >
          <div 
            style={{
              position: 'absolute',
              left: '-11px',
              top: '0',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: index === 0 ? '#1976d2' : '#9e9e9e',
            }}
          />
          <div style={{ paddingLeft: '20px' }}>
            <Typography variant="body2" color="text.secondary">
              {new Date(milestone.date).toLocaleDateString()}
            </Typography>
            <Typography variant="body1">
              {milestone.title}
            </Typography>
            {milestone.description && (
              <Typography variant="body2">
                {milestone.description}
              </Typography>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const CaseTimeline = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const { API_BASE_URL } = config;
  const selectedCompanyId = useSelector((state) => state.kyc.selectedCompanyId);

  // Fetch cases from API
  const fetchCases = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/api/v1/legalcases/${selectedCompanyId}`);
      setCases(response.data.data || []);
    } catch (error) {
      setError("Error fetching cases: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCompanyId) {
      fetchCases();
    }
  }, [selectedCompanyId]);

  const handleCaseSelect = (caseItem) => {
    setSelectedCase(caseItem);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Generate milestone data from case history
  const generateMilestones = (caseItem) => {
    const milestones = [
      {
        date: caseItem.openDate,
        title: "Case Initiated",
        description: `Case ${caseItem.id} opened - ${caseItem.type}`,
      },
      {
        date: caseItem.lastUpdated,
        title: `Status: ${caseItem.status}`,
        description: caseItem.description,
      },
    ];

    // Add closing date if exists
    if (caseItem.closingDate) {
      milestones.push({
        date: caseItem.closingDate,
        title: "Case Closed",
        description: "Case resolution completed",
      });
    }

    return milestones.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  // Generate financial data for chart
  const generateFinancialData = (caseItem) => {
    // This is a placeholder - you'll need to modify this based on your actual financial data structure
    return {
      labels: ["Open Date", "Last Updated"],
      datasets: [
        {
          label: "Case Progress",
          data: [0, 100],
          borderColor: "rgb(75, 192, 192)",
          tension: 0.1,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Case Progress Timeline",
      },
    },
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg">
        <Typography variant="h4" gutterBottom>
          Legal Case Timeline
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {cases.map((caseItem) => (
            <Grid item xs={12} md={6} key={caseItem._id}>
              <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6">
                  {caseItem.client} - {caseItem.id}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Status: {caseItem.status}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  Type: {caseItem.type}
                </Typography>

                <CustomTimeline milestones={generateMilestones(caseItem)} />

                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleCaseSelect(caseItem)}
                  sx={{ mt: 2 }}
                >
                  View Details
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          {selectedCase && (
            <>
              <DialogTitle>
                {selectedCase.client} - {selectedCase.id} Details
              </DialogTitle>
              <DialogContent>
                <Box sx={{ mt: 2, mb: 4 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Case Type: {selectedCase.type}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    Priority: {selectedCase.priority}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    Assigned To: {selectedCase.assignedTo}
                  </Typography>
                </Box>

                <Box sx={{ height: 300, width: "100%", mb: 2 }}>
                  <Line
                    data={generateFinancialData(selectedCase)}
                    options={chartOptions}
                  />
                </Box>
                
                <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Case Timeline</Typography>
                <CustomTimeline milestones={generateMilestones(selectedCase)} />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog} color="primary">
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Container>
    </Layout>
  );
};

export default CaseTimeline;
